import _ from 'lodash';

import {
  queryList,
  queryListInformation,
  queryListPermissions,
  querySavePermission,
  updateActionsDiagramPositions,
} from './service';
import { bindServices } from '@/services/api';
import { CoreEnums } from '@/utils/domain/constants';
import { generateActionConfigFromEnum } from './utils/actionConfigGenerator';
import { DEFAULT_TABS } from './tabsConfig';
import { SELECTOR_FIELDS, BOOL_FIELDS, selectorToForm } from './config/actionFieldMap';

const api = bindServices('processActionConfig');

// Shared by both LOAD reducers — keep in sync to avoid drift.
function mapActionPayloadToForm(payload) {
  let mappedType = payload.type;
  if (typeof payload.type === 'string') {
    const actionTypeKey = Object.keys(CoreEnums.ActionTypeString).find(
      (key) => CoreEnums.ActionTypeString[key] === payload.type,
    );
    mappedType = actionTypeKey ? CoreEnums.ActionType[actionTypeKey] : payload.type;
  }

  const result = {
    ...payload,
    type: mappedType,
    ...(payload.errorHandler && {
      isErrorControlEnabled: !!payload.errorHandler.enabled,
      ...(payload.errorHandler.errorDestination != null && {
        errorValueDestination: payload.errorHandler.errorDestination,
      }),
      ...(payload.errorHandler.nextAction && {
        nextActionIfErrorId: {
          label:
            payload.errorHandler.nextAction.name || payload.errorHandler.nextAction.displayText,
          value: payload.errorHandler.nextAction.id,
          ...payload.errorHandler.nextAction,
        },
      }),
      ...(payload.errorHandler.destinationForm && {
        errorFormDestinationId: {
          label:
            payload.errorHandler.destinationForm.name ||
            payload.errorHandler.destinationForm.displayText,
          value: payload.errorHandler.destinationForm.id,
          ...payload.errorHandler.destinationForm,
        },
      }),
      ...(payload.errorHandler.destinationField && {
        errorFieldDestinationId: {
          label:
            payload.errorHandler.destinationField.displayText ||
            payload.errorHandler.destinationField.name,
          value: payload.errorHandler.destinationField.id,
          ...payload.errorHandler.destinationField,
        },
      }),
      ...(payload.errorHandler.destinationVariable && {
        errorVariableDestinationId: {
          label:
            payload.errorHandler.destinationVariable.internalName ||
            payload.errorHandler.destinationVariable.name,
          value: payload.errorHandler.destinationVariable.id,
          ...payload.errorHandler.destinationVariable,
        },
      }),
    }),
    ...(mappedType === CoreEnums.ActionType.SendSMS &&
      payload.notifications?.sms && {
        ...(payload.notifications.sms.connection && {
          connectionId: {
            label:
              payload.notifications.sms.connection.name ||
              payload.notifications.sms.connection.displayText,
            value: payload.notifications.sms.connection.id,
            ...payload.notifications.sms.connection,
          },
        }),
        smsContent: payload.notifications.sms.content,
      }),
    ...(mappedType === CoreEnums.ActionType.RunCode &&
      payload.code && { code: { attachments: [payload.code] } }),
    ...(mappedType === CoreEnums.ActionType.WaitForChildRequests && {
      ...(payload.scope != null && { waitScope: payload.scope }),
      ...(payload.completionMode != null && { waitCompletionMode: payload.completionMode }),
      ...(payload.timeoutBehavior != null && { waitTimeoutBehavior: payload.timeoutBehavior }),
      ...(payload.noChildrenBehavior != null && {
        waitNoChildrenBehavior: payload.noChildrenBehavior,
      }),
      ...(payload.childrenFailureBehavior != null && {
        waitChildrenFailureBehavior: payload.childrenFailureBehavior,
      }),
      ...(payload.maxWaitTimeUnit != null && { maxWaitTimeUnit: payload.maxWaitTimeUnit }),
      ...(payload.maxWaitTimeValue != null && { maxWaitTimeValue: payload.maxWaitTimeValue }),
      ...(payload.startAction && {
        waitStartActionId: {
          label: payload.startAction.name || payload.startAction.displayText,
          value: payload.startAction.id,
          ...payload.startAction,
        },
      }),
      ...(payload.targetAction && {
        waitTargetActionId: {
          label: payload.targetAction.name || payload.targetAction.displayText,
          value: payload.targetAction.id,
          ...payload.targetAction,
        },
      }),
    }),
  };

  // After the blocks above, so raw `connection` overrides the SendSMS-derived connectionId.
  for (const { formField, backendKey, labelKey } of SELECTOR_FIELDS) {
    if (payload[backendKey]) result[formField] = selectorToForm(payload[backendKey], labelKey);
  }
  for (const { formField, backendKey } of BOOL_FIELDS) {
    if (backendKey in payload) result[formField] = payload[backendKey];
  }

  return result;
}

const meta = {
  page: 1,
  limit: 5,
  count: 0,
  totalPages: 0,
};

const initialState = {
  newAction: false,
  selectedItem: null,
  configSelectedItem: null,
  formConfig: null,
  showConfig: false,
  list: [],
  listConfig: [],
  dataInfomation: null,
  listAll: [],
  actionPermission: null,
  pagination: meta,
  view: false,
  orderBy: '',
  id: null,
  loadingItem: false,
  type: null,
  drawer: false,
  success: false,
  tab: '',
  activeActionId: null, // Track currently open action for token refresh
  notes: [], // Store diagram notes from server
  autoBeautify: false, // Flag to trigger automatic layout when diagram positions are missing
  ListTab: null,
  tabs: DEFAULT_TABS,
  history: [],
  currentIndex: -1,
  latestSaveIndex: -1,
  generateIA: {},
  defaultFieldsValue: null,
};

/**
 * Shared logic for both updateDiagramPositions and updateDiagramPositionsAndRefresh.
 * Validates inputs, guards on write-mode, transforms positions, and PATCHes to server.
 * Returns the patched diagramData on success, or null if skipped.
 */
async function patchDiagramPositions({
  processId,
  versionId,
  token,
  positions,
  isWriteMode,
  updateFn,
}) {
  if (!processId || !versionId || !token || !Array.isArray(positions) || positions.length === 0) {
    return null;
  }
  if (!isWriteMode) return null;

  const diagramData = positions.map((position) => ({
    id: position.id,
    diagramData: {
      x: position.x,
      y: position.y,
      collapsed: true, // Always true as specified
    },
  }));

  await updateFn({ processId, versionId, token, data: diagramData });
  return diagramData;
}

const Model = {
  namespace: 'processActionConfig',
  state: initialState,
  effects: {
    *initializeActionsConfig({ payload }, { put }) {
      try {
        // Initialize action configuration list from ActionType enum
        const { formatMessage } = payload;
        const actionConfigs = generateActionConfigFromEnum(formatMessage);
        yield put({ type: 'queryActionsConfig', payload: actionConfigs });
      } catch (error) {
        console.error('[processActionConfig/initializeActionsConfig]', error);
      }
    },

    *fetchDiagramByToken({ payload }, { call, put }) {
      try {
        const response = yield call(api.apiRoute, {
          route: `process/config/${payload.processId}/${payload.versionId}/diagram`,
          params: { token: payload.token },
        });
        const actions = Array.isArray(response?.actions) ? response.actions : [];
        const shouldBeautify =
          actions.length > 0 &&
          actions.every((action) => {
            const diagramData = action?.diagramData;
            if (!diagramData) return true;
            const hasX = !_.isNil(diagramData.x) || !_.isNil(diagramData.xCoordinate);
            const hasY = !_.isNil(diagramData.y) || !_.isNil(diagramData.yCoordinate);
            return !(hasX || hasY);
          });

        yield put({ type: 'queryActionsFromDiagram', payload: response });
        yield put({ type: 'setAutoBeautify', payload: shouldBeautify });
      } catch (error) {
        console.error('[processActionConfig/fetchDiagramByToken]', error);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'error', message: error?.message },
        });
      }
    },

    *updateDiagramPositions({ payload }, { call, select, put }) {
      try {
        // Prefer payload values, fallback to store (prevents missing token when a memoized component
        // dispatches with stale props).
        const storeGeneratedToken = yield select((s) => s.processConfig.generatedToken);
        const storeProcessId = yield select((s) => s.processConfig.processId);
        const processId = payload.processId ?? storeProcessId;
        const versionId = payload.versionId ?? storeGeneratedToken?.versionId;
        const token = payload.token ?? storeGeneratedToken?.token;
        const processConfigState = yield select((state) => state.processConfig);
        const isWriteMode = processConfigState.accessMode === CoreEnums.AccessMode.Write;

        const result = yield call(patchDiagramPositions, {
          processId,
          versionId,
          token,
          positions: payload.positions,
          isWriteMode,
          updateFn: updateActionsDiagramPositions,
        });

        if (result) {
          // After successful PATCH, update local state to maintain sync
          yield put({ type: 'updateLocalPositions', payload: payload.positions });
        }
      } catch (error) {
        console.error('[processActionConfig/updateDiagramPositions]', error);
      }
    },

    *updateDiagramPositionsAndRefresh({ payload }, { call, select, put }) {
      try {
        // Prefer payload values, fallback to store (prevents missing token when a memoized component
        // dispatches with stale props).
        const storeGeneratedToken = yield select((s) => s.processConfig.generatedToken);
        const storeProcessId = yield select((s) => s.processConfig.processId);
        const processId = payload.processId ?? storeProcessId;
        const versionId = payload.versionId ?? storeGeneratedToken?.versionId;
        const token = payload.token ?? storeGeneratedToken?.token;
        const processConfigState = yield select((state) => state.processConfig);
        const isWriteMode = processConfigState.accessMode === CoreEnums.AccessMode.Write;

        const result = yield call(patchDiagramPositions, {
          processId,
          versionId,
          token,
          positions: payload.positions,
          isWriteMode,
          updateFn: updateActionsDiagramPositions,
        });

        if (result) {
          // After successful PATCH, refresh diagram from server to ensure consistency
          yield new Promise((resolve) => setTimeout(resolve, 200)); // Slightly longer delay for beautify
          const diagramResponse = yield call(api.apiRoute, {
            route: `process/config/${processId}/${versionId}/diagram`,
            params: { token },
          });

          yield put({ type: 'queryActionsFromDiagram', payload: diagramResponse });
        }
      } catch (error) {
        console.error('[processActionConfig/updateDiagramPositionsAndRefresh]', error);
      }
    },

    *updateNotesDiagramPositions({ payload }, { call, select, put }) {
      try {
        const { processId, versionId, token, positions } = payload;

        // Transform positions to diagram data format
        const diagramData = positions.map(({ id, x, y }) => ({ id, diagramData: { x, y } }));

        // Only update if we're in Write mode
        const processConfigState = yield select((state) => state.processConfig);
        const isWriteMode = processConfigState.accessMode === CoreEnums.AccessMode.Write;

        if (isWriteMode) {
          yield call(api.apiRoute, {
            route: `process/config/${processId}/${versionId}/diagram/notes`,
            method: 'patch',
            params: { token },
            data: diagramData,
          });

          // After successful PATCH, update local state to maintain sync
          yield put({ type: 'updateLocalNotePositions', payload: positions });
        }
      } catch (error) {
        console.error('[processActionConfig/updateNotesDiagramPositions]', error);
      }
    },

    *updateNoteDiagramPosition({ payload }, { call, select }) {
      try {
        const { processId, versionId, token, noteId, position } = payload;

        // Only update if we're in Write mode
        const processConfigState = yield select((state) => state.processConfig);
        const isWriteMode = processConfigState.accessMode === CoreEnums.AccessMode.Write;

        if (isWriteMode) {
          yield call(api.apiRoute, {
            route: `process/config/${processId}/${versionId}/diagram/notes/${noteId}`,
            method: 'patch',
            params: { token },
            data: { diagramData: { x: position.x, y: position.y } },
          });
        }
      } catch (error) {
        console.error('[processActionConfig/updateNoteDiagramPosition]', error);
      }
    },

    *updateNoteContent({ payload }, { call, select, put }) {
      try {
        const { processId, versionId, token, noteId, text, position } = payload;

        // Only update if we're in Write mode
        const processConfigState = yield select((state) => state.processConfig);
        const isWriteMode = processConfigState.accessMode === CoreEnums.AccessMode.Write;

        if (isWriteMode) {
          const requestData = {
            elementType: 'note',
            itemId: noteId,
            data: {
              notes: text,
              diagramData: {
                x: position.x,
                y: position.y,
              },
            },
          };

          const response = yield call(api.apiRoute, {
            route: `process/config/modify/${processId}/${versionId}`,
            method: 'post',
            params: { token },
            data: requestData,
          });

          // If successful, refresh the diagram to get the updated data
          if (response && (response.data || response.success !== false)) {
            const diagramResponse = yield call(api.apiRoute, {
              route: `process/config/${processId}/${versionId}/diagram`,
              params: { token },
            });

            if (diagramResponse) {
              yield put({ type: 'queryActionsFromDiagram', payload: diagramResponse });
            }
          }
        }
      } catch (error) {
        console.error('[processActionConfig/updateNoteContent]', error);
      }
    },

    *deleteNoteDiagram({ payload }, { call, select, put }) {
      try {
        const { processId, versionId, token, noteId } = payload;

        // Only delete if we're in Write mode
        const processConfigState = yield select((state) => state.processConfig);
        const isWriteMode = processConfigState.accessMode === CoreEnums.AccessMode.Write;

        if (isWriteMode) {
          const requestData = {
            elementType: 'note',
            itemId: noteId,
            force: true,
          };

          const response = yield call(api.apiRoute, {
            route: `process/config/remove/${processId}/${versionId}`,
            method: 'post',
            params: { token },
            data: requestData,
          });

          // If successful, remove the note from local state (optimistic update)
          if (response !== null && response !== undefined) {
            yield put({ type: 'removeNoteFromList', payload: { noteId } });
          }
        }
      } catch (error) {
        console.error('[processActionConfig/deleteNoteDiagram]', error);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: error?.message,
          },
        });
      }
    },

    *appendActionToDiagram({ payload }, { call, select, put }) {
      try {
        const { processId, versionId, token, actionType, position, name } = payload;

        // Only append if we're in Write mode
        const processConfigState = yield select((state) => state.processConfig);
        const isWriteMode = processConfigState.accessMode === CoreEnums.AccessMode.Write;

        if (isWriteMode) {
          const requestData = {
            elementType: 'action',
            data: {
              enabled: true,
              type: actionType,
              diagramData: {
                x: position.x,
                y: position.y,
              },
            },
          };
          if (name) {
            requestData.data.name = name;
          }

          const response = yield call(api.apiRoute, {
            route: `process/config/append/${processId}/${versionId}`,
            method: 'post',
            params: { token },
            data: requestData,
          });

          // If successful, add the new action to the list optimistically
          if (response && response.newItemId) {
            yield put({
              type: 'addServerActionToList',
              payload: response.data,
            });
          }
        }
      } catch (error) {
        console.error('[processActionConfig/appendActionToDiagram]', error);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: error?.message,
          },
        });
      }
    },

    *appendNoteToDiagram({ payload }, { call, select, put }) {
      try {
        const { processId, versionId, token, position, text } = payload;

        // Only append if we're in Write mode
        const processConfigState = yield select((state) => state.processConfig);
        const isWriteMode = processConfigState.accessMode === CoreEnums.AccessMode.Write;

        if (isWriteMode) {
          // Generate a temporary GUID for the new note
          const tempId = crypto.randomUUID();

          const requestData = {
            elementType: 'note',
            itemId: tempId.toUpperCase(),
            data: {
              notes: text || '',
              diagramData: {
                x: position.x,
                y: position.y,
              },
            },
          };

          const response = yield call(api.apiRoute, {
            route: `process/config/append/${processId}/${versionId}`,
            method: 'post',
            params: { token },
            data: requestData,
          });

          // If successful, refresh the diagram to get the updated data
          if (response && (response.newItemId || response.data)) {
            // Query the diagram again to get the complete updated data
            const diagramResponse = yield call(api.apiRoute, {
              route: `process/config/${processId}/${versionId}/diagram`,
              params: { token },
            });

            if (diagramResponse) {
              yield put({
                type: 'queryActionsFromDiagram',
                payload: diagramResponse,
              });
            }
          }
        }
      } catch (error) {
        console.error('[processActionConfig/appendNoteToDiagram]', error);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: error?.message,
          },
        });
      }
    },

    *setNextAction({ payload }, { call, select, put }) {
      try {
        const { processId, versionId, token, fromActionId, toActionId, isFalseConnection } =
          payload;

        // Only update if we're in Write mode
        const processConfigState = yield select((state) => state.processConfig);
        const isWriteMode = processConfigState.accessMode === CoreEnums.AccessMode.Write;

        if (isWriteMode) {
          // Get the target action data
          const currentState = yield select((state) => state.processActionConfig);
          const toAction = currentState.list.find((action) => action.id === toActionId);

          if (toAction) {
            // Map enum type back to string using ActionTypeString
            const actionTypeKey = Object.keys(CoreEnums.ActionType).find(
              (key) => CoreEnums.ActionType[key] === toAction.type,
            );
            const typeString = actionTypeKey
              ? CoreEnums.ActionTypeString[actionTypeKey]
              : 'complete-information';

            const requestData = {
              nextAction: {
                id: toAction.id,
                name: toAction.name || toAction.displayTitle,
                type: typeString,
              },
            };

            // Send PATCH to server
            yield call(api.apiRoute, {
              route: `process/config/${processId}/${versionId}/actions/setNextActions/${fromActionId}`,
              method: 'patch',
              params: { token },
              data: requestData,
            });

            // Update local state to reflect the connection immediately
            const updatedList = currentState.list.map((action) => {
              if (action.id === fromActionId) {
                // Update the appropriate connection field based on connection type
                if (isFalseConnection) {
                  return {
                    ...action,
                    nextActionIfFalseId: toActionId,
                  };
                } else {
                  return {
                    ...action,
                    nextActionId: toActionId,
                  };
                }
              }
              return action;
            });

            yield put({
              type: 'updateConnectionInList',
              payload: updatedList,
            });
          }
        }
      } catch (error) {
        console.error('[processActionConfig/setNextAction]', error);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: error?.message,
          },
        });
      }
    },

    *removeActionFromDiagram({ payload }, { call, select, put }) {
      try {
        const { processId, versionId, token, actionId } = payload;

        // Only remove if we're in Write mode
        const processConfigState = yield select((state) => state.processConfig);
        const isWriteMode = processConfigState.accessMode === CoreEnums.AccessMode.Write;

        if (isWriteMode) {
          const requestData = {
            elementType: CoreEnums.BuilderElementType.Action,
            itemId: actionId,
            force: true,
          };

          const response = yield call(api.apiRoute, {
            route: `process/config/remove/${processId}/${versionId}`,
            method: 'post',
            params: { token },
            data: requestData,
          });

          // If successful, remove the action from local state
          if (response !== null && response !== undefined) {
            yield put({
              type: 'removeActionFromList',
              payload: { actionId },
            });
          }
        }
      } catch (error) {
        console.error('[processActionConfig/removeActionFromDiagram]', error);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: error?.message,
          },
        });
      }
    },

    *fetchList({ payload }, { call, put }) {
      try {
        const response = yield call(queryList, payload);
        yield put({
          type: 'queryActions',
          payload: response,
        });
      } catch (error) {
        console.error('[processActionConfig/fetchList]', error);
      }
    },

    *fetchGenerateIA({ payload }, { call, put }) {
      try {
        const response = yield call(api.apiRoute, {
          route: `processEngine/diagram/generate/${payload.processId}`,
          method: 'post',
          data: payload.params,
        });
        yield put({
          type: 'queryGenerateIA',
          payload: response,
        });
      } catch (error) {
        console.error('[processActionConfig/fetchGenerateIA]', error);
      }
    },

    *initAddHistory({ put }, { payload }) {
      yield put({ type: 'addState', payload });
    },

    *fetchSaveConfigList({ payload }, { call, put, select }) {
      try {
        const history = yield select((state) => state.processActionConfig.history);
        const data = history[history.length - 1]
          ?.filter((l) => l.updated === true && !l.id.startsWith('action_'))
          ?.map((l) => ({
            id: l.id,
            nextActionId: l.nextActionId,
            ...(l.nextActionIfFalseId && { nextActionIfFalseId: l.nextActionIfFalseId }),
            diagramData: {
              xCoordinate: l.x,
              yCoordinate: l.y,
            },
          }));
        if (!data?.length) return;
        const response = yield call(api.apiRoute, {
          route: `process/updateActionsDiagram/${payload.processId}`,
          method: 'post',
          data,
        });
      } catch (error) {
        console.error('[processActionConfig/fetchSaveConfigList]', error);
      }
    },

    *getItemConfigAction({ payload }, { call, put, select }) {
      try {
        // Prefer payload values, fallback to store
        const storeGeneratedToken = yield select((s) => s.processConfig.generatedToken);
        const storeProcessId = yield select((s) => s.processConfig.processId);
        const processId = payload.processId ?? storeProcessId;
        const versionId = payload.versionId ?? storeGeneratedToken?.versionId;
        const token = payload.token ?? storeGeneratedToken?.token;

        if (!processId || !versionId || !token || !payload.id) {
          return;
        }

        const response = yield call(api.apiRoute, {
          route: `process/config/${processId}/${versionId}/actions/${payload.id}`,
          params: { token },
        });
        // Set data for tabs visibility and for configuration form
        yield put({ type: 'queryItemActionsList', payload: response });
        yield put({ type: 'queryItemActionsConfig', payload: response });
        yield put({ type: 'activeTabs', payload: 'configurations' });
      } catch (error) {
        console.error('[processActionConfig/getItemConfigAction]', error);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'error',
            message: error?.message,
          },
        });
      }
    },

    *fetchListInformation({ payload }, { call, put }) {
      try {
        const response = yield call(queryListInformation, payload);
        yield put({
          type: 'queryInformation',
          payload: {
            data: response,
            name: payload.name,
            field: payload.field,
          },
        });
      } catch (error) {
        console.error('[processActionConfig/fetchListInformation]', error);
      }
    },

    *fetchListCondition({ payload }, { call, put }) {
      try {
        const response = yield call(queryListInformation, payload);
        yield put({
          type: 'queryCondiion',
          payload: {
            data: response,
            name: payload.name,
            field: payload.field,
          },
        });
      } catch (error) {
        console.error('[processActionConfig/fetchListCondition]', error);
      }
    },

    *addNewPermission({ payload }, { put }) {
      yield put({
        type: 'addPermission',
        payload,
      });
    },

    *addListPermission({ payload }, { put }) {
      yield put({
        type: 'addListPermissions',
        payload,
      });
    },

    *deleteItemPermision({ payload }, { put }) {
      yield put({ type: 'deleteItemPermisions', payload });
      yield put({ type: `global/modalConfirm`, state: false, id: null });
    },

    *fetchListPermissions({ payload }, { call, put }) {
      try {
        const response = yield call(queryListPermissions, payload);
        yield put({
          type: 'queryPermission',
          payload: {
            data: response,
            name: payload.name,
            field: payload.field,
          },
        });
      } catch (error) {
        console.error('[processActionConfig/fetchListPermissions]', error);
      }
    },

    *savePermission({ payload }, { call, put }) {
      try {
        yield call(querySavePermission, payload);
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({
          type: 'saveSuccessPermission',
          payload: { state: false, item: null },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: false,
              visibleForm: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      }
    },

    *saveInformation({ payload }, { call, put }) {
      try {
        yield call(api.apiRoute, {
          route: `action/updateCompleteInformationFormFieldMappings/${payload.actionId}/${payload.formId}`,
          method: 'post',
          data: payload.data,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({
          type: 'addInformation',
          payload: { state: false, item: null },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: false,
              visibleForm: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      }
    },

    *querySaveInformationCondition({ payload }, { call, put }) {
      try {
        yield call(api.apiRoute, {
          route: `action/configConditions/${payload.actionId}`,
          method: 'post',
          data: payload.data,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({
          type: 'addInformation',
          payload: { state: false, item: null },
        });
      } catch (error) {
        if (error.message) {
          yield put({
            type: 'global/showNotificationsGlobal',
            payload: {
              visible: false,
              visibleForm: true,
              type: 'error',
              message: error.message,
            },
          });
        }
      }
    },

    *addNewAction({ payload }, { put }) {
      yield put({ type: 'addAction', payload: { ...payload } });
      if (!_.isNil(payload.id)) {
        yield put({ type: 'editOrViewAction', payload });
      }
    },

    *saveNewAction({ payload }, { call, put }) {
      try {
        const response = yield call(api.apiRoute, {
          route: 'action',
          method: 'post',
          data: payload.data,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'saveAction', payload: response });
        yield put({ type: 'fetchList', payload: payload.params });
      } catch (error) {
        if (error && error.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
            validationErrors: error.validationErrors,
          });
        }
      }
    },

    *saveNewActionBySteps({ payload }, { call, put }) {
      try {
        const response = yield call(api.apiRoute, {
          route: 'action',
          method: 'post',
          data: payload.data,
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
          },
        });

        _.isFunction(payload.onFinish) && payload.onFinish(response);
      } catch (error) {
        if (error && error.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
            validationErrors: error.validationErrors,
          });
        }
      }
    },

    *saveNewActionFromList({ payload }, { call, put, select }) {
      try {
        const storeGeneratedToken = yield select((s) => s.processConfig.generatedToken);
        const storeProcessId = yield select((s) => s.processConfig.processId);
        const processId = payload.processId ?? storeProcessId;
        const versionId = payload.versionId ?? storeGeneratedToken?.versionId;
        const token = payload.token ?? storeGeneratedToken?.token;

        let response;
        if (processId && versionId && token) {
          response = yield call(api.apiRoute, {
            route: `process/config/append/${processId}/${versionId}`,
            method: 'post',
            params: { token },
            data: payload.data,
          });
          if (response?.newItemId) {
            yield put({ type: 'addServerActionToList', payload: response.data });
          }
        } else {
          response = yield call(queryNewAction, payload.data);
        }

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        _.isFunction(payload.onFinish) && payload.onFinish(response);
      } catch (error) {
        if (error && error.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: error.message },
            validationErrors: error.validationErrors,
          });
        }
      }
    },

    *saveEditActionFromList({ payload }, { call, put, select }) {
      try {
        const storeGeneratedToken = yield select((s) => s.processConfig.generatedToken);
        const storeProcessId = yield select((s) => s.processConfig.processId);
        const processId = payload.processId ?? storeProcessId;
        const versionId = payload.versionId ?? storeGeneratedToken?.versionId;
        const token = payload.token ?? storeGeneratedToken?.token;

        let response;
        if (processId && versionId && token) {
          response = yield call(api.apiRoute, {
            route: `process/config/modify/${processId}/${versionId}`,
            method: 'post',
            params: { token },
            data: payload.data,
          });
          yield put({ type: 'fetchDiagramByToken', payload: { processId, versionId, token } });
        } else {
          response = yield call(api.apiRoute, {
            route: 'action',
            method: 'put',
            data: payload.data,
          });
          yield put({ type: 'fetchList', payload: payload.params });
        }

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
        yield put({ type: 'saveAction', payload: response });
      } catch (error) {
        if (error && error.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: error.message },
            validationErrors: error.validationErrors,
          });
        }
      }
    },

    *saveConfigAction({ payload }, { call, put }) {
      try {
        // Use the new modify endpoint if builder tokens are provided
        let response;
        if (payload.processId && payload.versionId && payload.token) {
          response = yield call(api.apiRoute, {
            route: `process/config/modify/${payload.processId}/${payload.versionId}`,
            method: 'post',
            params: { token: payload.token },
            data: payload.data,
          });
        } else {
          // Fallback to old endpoint for backward compatibility
          response = yield call(api.apiRoute, {
            route: 'action',
            method: 'put',
            data: payload.data,
          });
        }

        yield put({
          type: 'global/showNotificationsGlobal',
          payload: {
            visible: true,
            type: 'success',
          },
        });
        yield put({
          type: 'saveAction',
          payload: response,
        });

        // Only fetch list if NOT in builder context
        if (!payload.processId || !payload.versionId || !payload.token) {
          yield put({
            type: 'fetchList',
            payload: payload.params,
          });
        } else {
          // In builder context, refresh the diagram instead
          yield put({
            type: 'fetchDiagramByToken',
            payload: {
              processId: payload.processId,
              versionId: payload.versionId,
              token: payload.token,
            },
          });
        }
      } catch (error) {
        if (error && error.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: {
              visible: true,
              type: 'error',
              message: error.message,
            },
            validationErrors: error.validationErrors,
          });
        }
      }
    },

    *searchCriteria({ payload }, { put }) {
      try {
        yield put({
          type: 'searchCriterias',
          payload,
        });
      } catch (error) {
        console.error('[processActionConfig/searchCriteria]', error);
      }
    },

    *updateNode({ payload }, { put }) {
      try {
        yield put({
          type: 'updateNodes',
          payload,
        });
      } catch (error) {
        console.error('[processActionConfig/updateNode]', error);
      }
    },

    *showConfigAction({ payload }, { put }) {
      try {
        yield put({
          type: 'showConfigActions',
          payload,
        });
      } catch (error) {
        console.error('[processActionConfig/showConfigAction]', error);
      }
    },

    *updateNextActionId({ payload }, { put }) {
      try {
        yield put({
          type: 'updateNextActionIds',
          payload,
        });
      } catch (error) {
        console.error('[processActionConfig/updateNextActionId]', error);
      }
    },

    *activeTab({ payload }, { put }) {
      yield put({ type: 'activeTabs', payload });
    },

    *clearSelectedItem({ payload }, { put }) {
      yield put({ type: 'clearSelectedItems', payload });
    },

    *hideAllActions({ payload = 'hideActions' }, { put }) {
      yield put({ type: payload });
    },
  },

  reducers: {
    resetConfig(state) {
      return {
        ...initialState,
      };
    },

    clearActiveAction(state) {
      return {
        ...state,
        activeActionId: null,
        selectedItem: null,
      };
    },

    queryActionsFromDiagram(state, { payload }) {
      // payload now comes as an object with { actions: [...], notes: [...] }
      const typeMap = {
        'default-initial': CoreEnums.ActionType.DefaultInitial,
        'complete-information': CoreEnums.ActionType.CompleteInformation,
        approval: CoreEnums.ActionType.Approval,
        'auto-approve': CoreEnums.ActionType.AutoApprove,
        conditional: CoreEnums.ActionType.Conditional,
        'execute-task': CoreEnums.ActionType.ExecuteTask,
        'execute-api-call': CoreEnums.ActionType.ExecuteApiCall,
        'auto-reject': CoreEnums.ActionType.AutoReject,
        'digital-signature': CoreEnums.ActionType.DigitalSignature,
        'send-email': CoreEnums.ActionType.SendEmail,
        'execute-query': CoreEnums.ActionType.ExecuteQuery,
        'dynamic-entity-operation': CoreEnums.ActionType.DynamicEntityOperation,
        'generate-excel': CoreEnums.ActionType.GenerateExcel,
        'start-process-instance': CoreEnums.ActionType.StartProcessInstance,
        'set-variable': CoreEnums.ActionType.SetVariableData,
        'set-form-data': CoreEnums.ActionType.SetFormData,
        parallel: CoreEnums.ActionType.Parallel,
        'generate-documents': CoreEnums.ActionType.GenerateDocuments,
        'scheduled-execution': CoreEnums.ActionType.ScheduledExecution,
        'sharepoint-operation': CoreEnums.ActionType.SharePointOperation,
        iterate: CoreEnums.ActionType.Iterate,
        switch: CoreEnums.ActionType.Switch,
        'internal-operation': CoreEnums.ActionType.InternalOperation,
        'generate-plain-text-file': CoreEnums.ActionType.GeneratePlainTextFile,
        'import-plain-text-file': CoreEnums.ActionType.ImportPlainTextFile,
        'consume-sequence': CoreEnums.ActionType.ConsumeSequence,
        'execute-rpa-flow': CoreEnums.ActionType.ExecuteRpaFlow,
        integration: CoreEnums.ActionType.Integration,
        'auto-cancel': CoreEnums.ActionType.AutoCancel,
        'auto-void': CoreEnums.ActionType.AutoVoid,
        openai: CoreEnums.ActionType.OpenAI,
        'automy-ai': CoreEnums.ActionType.AutomyAI,
        'execute-process-action': CoreEnums.ActionType.ExecuteProcessAction,
        'google-integration': CoreEnums.ActionType.GoogleIntegration,
        'revert-cancellation': CoreEnums.ActionType.RevertCancellation,
        'revert-voiding': CoreEnums.ActionType.RevertVoiding,
        'ftp-operation': CoreEnums.ActionType.FTPOperation,
        'run-code': CoreEnums.ActionType.RunCode,
        'whatsapp-business-integration': CoreEnums.ActionType.WhatsAppBusinessIntegration,
        'microsoft-teams-integration': CoreEnums.ActionType.MicrosoftTeamsIntegration,
        'wait-for-child-requests': CoreEnums.ActionType.WaitForChildRequests,
        'send-sms': CoreEnums.ActionType.SendSMS,
      };

      // Extract actions array from payload object
      const actionsArray = payload?.actions || payload || [];
      const list = actionsArray.map((a) => {
        const type = typeMap[a.type] ?? CoreEnums.ActionType.CompleteInformation;
        const x =
          a.diagramData?.x ??
          a.diagramData?.xCoordinate ??
          (a.enabled ? Math.floor(Math.random() * 1400) + 50 : 0);
        const y =
          a.diagramData?.y ??
          a.diagramData?.yCoordinate ??
          (a.enabled ? Math.floor(Math.random() * 800) + 50 : 0);
        return {
          ...a,
          type,
          displayTitle: a.name || a.description || 'Sin nombre', // Generate displayTitle from name
          diagramData: { x, y },
          nextActionId: a.nextAction?.id,
          nextActionIfFalseId: a.nextActionIfFalse?.id ?? a.nextActionFalse?.id,
          // Add legacy properties for compatibility
          isDefaultInternalInitialCompleteInformationAction: a.type === 'default-initial',
          isInitialAction: a.type === 'default-initial',
        };
      });

      // Force a new array reference for notes to ensure React detects changes
      const notesArray = payload?.notes || [];
      const notes = [...notesArray];

      return {
        ...state,
        list,
        notes, // Store notes from diagram response with new array reference
      };
    },

    setAutoBeautify(state, { payload }) {
      return {
        ...state,
        autoBeautify: !!payload,
      };
    },

    queryActions(state, { payload }) {
      const data = payload.items || [];
      return {
        ...state,
        list: _.map(data, (l, key) => {
          return {
            ...l,
            diagramData: {
              x: l.isDefaultInternalInitialCompleteInformationAction
                ? 0
                : l.diagramData?.x ??
                  l.diagramData?.xCoordinate ??
                  Math.floor(Math.random() * (1500 - 50 + 1)) + 50,
              y: l.isDefaultInternalInitialCompleteInformationAction
                ? 0
                : l.diagramData?.y ??
                  l.diagramData?.yCoordinate ??
                  Math.floor(Math.random() * (1500 - 50 + 1)) + 50,
            },
          };
        }),
      };
    },

    queryGenerateIA(state, { payload }) {
      const data = payload.items || [];
      return {
        ...state,
        queryGenerateIA: {
          ...payload,
          actions: _.map(payload.actions, (l, key) => {
            return {
              ...l,
              diagramData: {
                x: l.isDefaultInternalInitialCompleteInformationAction
                  ? 0
                  : l.diagramData?.xCoordinate || Math.floor(Math.random() * (1500 - 50 + 1)) + 50,
                y: l.isDefaultInternalInitialCompleteInformationAction
                  ? 0
                  : l.diagramData?.yCoordinate || Math.floor(Math.random() * (1500 - 50 + 1)) + 50,
              },
            };
          }),
        },
      };
    },

    updateNodes(state, { payload, item, cords }) {
      return {
        ...state,
        list: _.map(state.list, (l) => {
          if (l.id === payload.item.id) {
            return {
              ...l,
              cords: payload.cords,
            };
          }
          return l;
        }),
      };
    },

    queryActionsConfig(state, { payload }) {
      return {
        ...state,
        listConfig: payload,
        listAll: payload,
      };
    },

    queryItemActionsList(state, { payload }) {
      const base = mapActionPayloadToForm(payload);
      const mapped = {
        ...base,
        // List-only UI-gating flags (not saved by the form; drive tab visibility):
        // SendEmail is always notification-driven (no toggle in its config) — surface
        // the notification tabs unconditionally.
        ...(base.type === CoreEnums.ActionType.SendEmail && { isNotificationsEnabled: true }),
        // GenerateDocuments is document-generation by purpose — surface the generation tab.
        ...(base.type === CoreEnums.ActionType.GenerateDocuments && {
          isPDFGenerationEnabled: true,
        }),
      };

      return {
        ...state,
        selectedItem: mapped,
        activeActionId: mapped.id, // Track the active action ID for token refresh
        ListTab: _.filter(state.tabs, (l) => {
          const isTypeMatch = _.includes(l.type, mapped.type);

          const hasFieldValue = !l.field || (l.field && mapped[l.field]);

          return l.name === 'configurations' || (isTypeMatch && hasFieldValue);
        }),
      };
    },

    queryItemActionsConfig(state, { payload }) {
      // Same nested→flat form mapping as queryItemActionsList, via the shared
      // pure mapper. The `if (payload)` guard preserves the original behavior of
      // passing a falsy payload straight through untouched.
      return {
        ...state,
        configSelectedItem: payload ? mapActionPayloadToForm(payload) : payload,
        success: false,
      };
    },

    addAction(state, { payload }) {
      return {
        ...state,
        newAction: payload.state,
        type: payload.type,
        defaultFieldsValue: payload.defaultFieldsValue,
        // a fresh add (no id) must not inherit the previously-visited action's config data
        ...(_.isNil(payload.id) && { id: null, selectedItem: null }),
      };
    },

    saveAction(state, { payload }) {
      return {
        ...state,
        saveAction: payload,
        newAction: false,
        success: true,
        selectedItem: null,
        tab: '',
      };
    },

    editAction(state, { payload }) {
      return {
        ...state,
        selectedItem: payload.item,
        view: payload.view,
        id: payload.id,
        success: false,
      };
    },

    showConfigActions(state, { payload }) {
      return {
        ...state,
        showConfig: false, // Force false - BladeStack replaces drawer system
        id: payload.id,
        selectedItem: null,
      };
    },

    editOrViewAction(state) {
      return {
        ...state,
        selectedItem: {},
      };
    },

    activeTabs(state, { payload }) {
      return {
        ...state,
        tab: payload,
      };
    },

    searchCriterias(state, { payload }) {
      return {
        ...state,
        listConfig: _.filter(state.listAll, (l) => {
          return (
            _.lowerCase(l.title).indexOf(_.lowerCase(payload)) > -1 ||
            _.lowerCase(l.description).indexOf(_.lowerCase(payload)) > -1
          );
        }),
      };
    },

    addPermission(state, { payload }) {
      return {
        ...state,
        newPermission: payload.state,
        newCustomList: false,
        selectedItem: payload.item,
      };
    },

    saveSuccessPermission(state, { payload }) {
      return {
        ...state,
        newPermission: payload.state,
        newCustomList: false,
        successPermission: true,
      };
    },

    addInformation(state, { payload }) {
      return {
        ...state,
        success: true,
      };
    },

    queryInformation(state, { payload }) {
      return {
        ...state,
        dataInfomation: _.map(payload.data, (l) => {
          return {
            order: Number(l.order),
            fieldId: l.field.id,
            fieldBehavior: l.fieldBehavior,
            logicalOperator: l.logicalOperator,
          };
        }),
      };
    },

    queryCondiion(state, { payload }) {
      return {
        ...state,
        dataCondition: payload.data,
      };
    },

    addListPermissions(state, { payload }) {
      return {
        ...state,
        [payload.name]: [...state[payload.name], payload.data],
      };
    },

    deleteItemPermisions(state, { payload }) {
      return {
        ...state,
        [payload.name]: _.filter(state[payload.name], (l) => l.id !== payload.id),
      };
    },

    queryPermission(state, { payload }) {
      return {
        ...state,
        actionDataPermission: _.map(payload.data, (l) => {
          return {
            ...l,
          };
        }),
      };
    },

    updateNextActionIds(state, { payload, item, cords }) {
      return {
        ...state,
        list: _.map(state.list, (l) => {
          if (l.id === payload.item.id) {
            return {
              ...l,
              nextActionId: payload.nextActionId,
            };
          }
          return l;
        }),
      };
    },

    updateLocalPositions(state, { payload }) {
      // Update only the specific positions without affecting other actions
      const positionsMap = new Map(payload.map((pos) => [pos.id, pos]));

      const updatedList = state.list.map((item) => {
        const newPosition = positionsMap.get(item.id);
        if (newPosition) {
          return {
            ...item,
            x: newPosition.x,
            y: newPosition.y,
            diagramData: {
              ...item.diagramData,
              x: newPosition.x,
              y: newPosition.y,
            },
          };
        }
        return item;
      });

      return {
        ...state,
        list: updatedList,
      };
    },

    updateLocalNotePositions(state, { payload }) {
      // Update only the specific note positions
      const positionsMap = new Map(payload.map((pos) => [pos.id, pos]));

      const updatedNotes = state.notes.map((note) => {
        const newPosition = positionsMap.get(note.id);
        if (newPosition) {
          return {
            ...note,
            diagramData: {
              ...note.diagramData,
              x: newPosition.x,
              y: newPosition.y,
            },
          };
        }
        return note;
      });

      return {
        ...state,
        notes: updatedNotes,
      };
    },

    updateConnectionInList(state, { payload }) {
      // Update the entire list with the new connection data
      return {
        ...state,
        list: payload,
      };
    },

    addServerNoteToList(state, { payload }) {
      // Add the new note to the existing notes list
      const newNote = {
        id: payload.id,
        notes: payload.notes || '',
        diagramData: {
          x: payload.diagramData?.x || 0,
          y: payload.diagramData?.y || 0,
          w: payload.diagramData?.w || 200,
          h: payload.diagramData?.h || 200,
        },
      };

      // Add only the new note to the existing list
      return {
        ...state,
        notes: [...state.notes, newNote],
      };
    },

    updateNotesFromServer(state, { payload }) {
      // Update notes from server response
      const notesArray = payload?.notes || [];

      return {
        ...state,
        notes: notesArray,
      };
    },

    addServerActionToList(state, { payload }) {
      // Map server type string to enum (same logic as queryActionsFromDiagram)
      const typeMap = {
        'default-initial': CoreEnums.ActionType.DefaultInitial,
        'complete-information': CoreEnums.ActionType.CompleteInformation,
        approval: CoreEnums.ActionType.Approval,
        'auto-approve': CoreEnums.ActionType.AutoApprove,
        conditional: CoreEnums.ActionType.Conditional,
        'execute-task': CoreEnums.ActionType.ExecuteTask,
        'execute-api-call': CoreEnums.ActionType.ExecuteApiCall,
        'auto-reject': CoreEnums.ActionType.AutoReject,
        'digital-signature': CoreEnums.ActionType.DigitalSignature,
        'send-email': CoreEnums.ActionType.SendEmail,
        'execute-query': CoreEnums.ActionType.ExecuteQuery,
        'dynamic-entity-operation': CoreEnums.ActionType.DynamicEntityOperation,
        'generate-excel': CoreEnums.ActionType.GenerateExcel,
        'start-process-instance': CoreEnums.ActionType.StartProcessInstance,
        'set-variable': CoreEnums.ActionType.SetVariableData,
        'set-form-data': CoreEnums.ActionType.SetFormData,
        parallel: CoreEnums.ActionType.Parallel,
        'generate-documents': CoreEnums.ActionType.GenerateDocuments,
        'scheduled-execution': CoreEnums.ActionType.ScheduledExecution,
        'sharepoint-operation': CoreEnums.ActionType.SharePointOperation,
        iterate: CoreEnums.ActionType.Iterate,
        switch: CoreEnums.ActionType.Switch,
        'internal-operation': CoreEnums.ActionType.InternalOperation,
        'generate-plain-text-file': CoreEnums.ActionType.GeneratePlainTextFile,
        'import-plain-text-file': CoreEnums.ActionType.ImportPlainTextFile,
        'consume-sequence': CoreEnums.ActionType.ConsumeSequence,
        'execute-rpa-flow': CoreEnums.ActionType.ExecuteRpaFlow,
        integration: CoreEnums.ActionType.Integration,
        'auto-cancel': CoreEnums.ActionType.AutoCancel,
        'auto-void': CoreEnums.ActionType.AutoVoid,
        openai: CoreEnums.ActionType.OpenAI,
        'automy-ai': CoreEnums.ActionType.AutomyAI,
        'execute-process-action': CoreEnums.ActionType.ExecuteProcessAction,
        'google-integration': CoreEnums.ActionType.GoogleIntegration,
        'revert-cancellation': CoreEnums.ActionType.RevertCancellation,
        'revert-voiding': CoreEnums.ActionType.RevertVoiding,
        'ftp-operation': CoreEnums.ActionType.FTPOperation,
        'run-code': CoreEnums.ActionType.RunCode,
        'whatsapp-business-integration': CoreEnums.ActionType.WhatsAppBusinessIntegration,
        'microsoft-teams-integration': CoreEnums.ActionType.MicrosoftTeamsIntegration,
        'wait-for-child-requests': CoreEnums.ActionType.WaitForChildRequests,
        'send-sms': CoreEnums.ActionType.SendSMS,
      };

      const mappedType = typeMap[payload.type] ?? CoreEnums.ActionType.CompleteInformation;
      const x = payload.diagramData?.x || 0;
      const y = payload.diagramData?.y || 0;

      const newAction = {
        ...payload,
        type: mappedType,
        displayTitle: payload.name || payload.description || 'Sin nombre',
        diagramData: { x, y },
        nextActionId: payload.nextAction?.id,
        nextActionIfFalseId: payload.nextActionIfFalse?.id ?? payload.nextActionFalse?.id,
        isDefaultInternalInitialCompleteInformationAction: payload.type === 'default-initial',
        isInitialAction: payload.type === 'default-initial',
        fromServer: true,
        isTemporary: false,
      };

      // Add only the new action to the existing list
      return {
        ...state,
        list: [...state.list, newAction],
      };
    },
    removeActionFromList(state, { payload }) {
      const { actionId } = payload;

      // Remove the action from the list
      const updatedList = state.list.filter((item) => item.id !== actionId);

      return {
        ...state,
        list: updatedList,
      };
    },

    removeNoteFromList(state, { payload }) {
      const { noteId } = payload;

      // Optimistically remove the note from the notes array
      const updatedNotes = state.notes.filter((note) => note.id !== noteId);

      return {
        ...state,
        notes: updatedNotes,
      };
    },

    hideActions(state) {
      return {
        ...state,
        newAction: false,
        list: [],
        listAll: [],
        listAllConfig: [],
        view: null,
        id: null,
        dataInfomation: null,
        success: false,
      };
    },
    clearSelectedItems(state) {
      return {
        ...state,
        configSelectedItem: null,
        success: false,
        tab: 'configurations', // Reset to default tab when clearing selected item
      };
    },
    hidePermission(state) {
      return {
        ...state,
        listDataPermission: null,
        successPermission: false,
      };
    },

    addState(state, { payload }) {
      const newState = { ...state };
      if (state.currentIndex < state.history.length - 1) {
        newState.history = newState.history.slice(0, state.currentIndex + 1);
      }
      newState.history.push(payload);
      newState.currentIndex++;
      return newState;
    },

    undo(state) {
      if (state.currentIndex > 0) {
        const newIndex = state.currentIndex - 1;
        const currentState = state.history[newIndex];
        return { ...state, currentIndex: newIndex, currentState };
      }
      return state;
    },
    redo(state) {
      if (state.currentIndex < state.history.length - 1) {
        const newIndex = state.currentIndex + 1;
        const currentState = state.history[newIndex];
        return { ...state, currentIndex: newIndex, currentState };
      }
      return state;
    },
    saveState(state) {
      return { ...state, latestSaveIndex: state.currentIndex };
    },

    resetState() {
      return initialState;
    },
  },
};

export default Model;
